import React, { isValidElement, useEffect, useMemo } from "react"
import { Course, getCourseBySlugLegacy, getCourseList, getFeedbackAvatarUrl } from "../../lib/prismic/courses"
import { Alert, Breadcrumb, Button, Col, List, Result, Row, Space, Spin, Typography } from "antd"
import { CourseTechnology, getFeedBacks, getLecturer, getTechnology } from "../../lib/prismic/mapData"
import { css } from "@emotion/react"
import Link from "next/link"
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from "next"
import { Layout } from "../../components/Layout/Layout"
import { SinglePage } from "../../components/SinglePage/SinglePage"
import { NextSeo } from "next-seo"
import { webUrl } from "../../config/web"
import styled from "@emotion/styled"
import { CourseType, CourseVariant, getCourseAgeGroupLabel } from "../../lib/prismic/Course"
import { PrismicDocument } from "@prismicio/types"
import { CommonArticles } from "../../components/blog/common"
import { useCourseCapacityStatus } from "../../lib/supabase/useCourseCapacityStatus"
import { viewItemEvent } from "../../lib/ga"
import { Section } from "../../components/Section"
import { SubscribeForm } from "../../components/SubscribeForm/SubscribeForm"
import { ContactForm } from "../../components/ContactForm/ContactForm"
import { md } from "../../styles/mediaQuery"
import { CheckCourseCapacity } from "../../lib/supabase/checkCourseCapacity"
import { ImageRounded } from "../../components/ImageRounded/ImageRounded"
import { PhotoGallery } from "../../components/PhotoGallery/PhotoGallery"
import { Comments } from "../../components/Comments/Comments"
import { MappedCourse } from "../../lib/prismic/MappedCourse"
import { slugifyCourse, slugifyTechnology } from "../../lib/prismic/common"
import { getCourse } from "../../lib/prismic/getCourse"
import { Ads } from "../../components/Ad/Ads"
import { AdsWithColors, getAllVzAds } from "../../lib/contentful/getAllVzAds"
import { SampleCourseVideo } from "../../components/RegisterModal/SampleCourseVideo"
import { getWebSiteUrlFromWindow } from "../../lib/url/getWebSiteUrlFromWindow"
import { RegisterPreliminaryForm } from "../../components/RegisterModal/RegisterPreliminaryForm"
import { RegisterFormForSampleCourse } from "../../components/RegisterModal/RegisterFormForSampleCourse"
import dynamic from "next/dynamic"
import { SmallScreenOnly } from "../../components/MobileVsDesktop/SmallScreenOnly"
import { AllProduct, getAllProducts } from "../../lib/contentful/product/getAllProducts"
import { getProductSlug } from "../../lib/contentful/product/getProductSlug"
import { extractSlugAndId } from "../../lib/contentful/product/extractSlugAndId"
import { getProductByEntryId } from "../../lib/contentful/product/getProductBySlug"
import { isArray } from "lodash"
import { Entry } from "contentful"
import { TypeProductEntitySkeleton } from "../../lib/contentful/ContentfulVzdelanibudoucnostiTypes"
import { dev } from "../../lib/dev"
import { getAvailableAndFilterOutProducts } from "../../lib/contentful/product/getAvailableProducts"
import { sortSimilarProducts } from "../../lib/contentful/sortSimilarProducts"
import EmailWithBreak from "../../components/EmailWithBreak/EmailWithBreak"
import { adjustTitleLength, generateSeoDescription } from "../../components/RegisterModal/generateSeoDescription"
import { CourseCloseAlert } from "../../components/RegisterModal/CourseCloseAlert"
import replaceUrlsWithLinks from "../../components/RegisterModal/replaceUrlsWithLinks"
import MiniScheduleCalendar from "../../components/ClassScheduleCalendar/MiniScheduleCalendar"
import { CoursePrice } from "../../components/CourseDetail/CoursePrice"
import { SimilarOrRelatedProducts } from "../../components/CourseDetail/SimilarOrRelatedProducts"
import { StyledParagraph } from "../../components/StyledParagraph/StyledParagraph"

const RegistrationFormWithSteps = dynamic(() => import("../../components/RegisterModal/RegisterFormWithSteps").then((mod) => mod.RegistrationFormWithSteps), {
  loading: () => <Spin />
})

const CoursesList = dynamic(() => import("../../components/CoursesList/CoursesList").then((mod) => mod.CoursesList), {
  loading: () => <Spin />
})

const NoSSREmbed = dynamic(() => import("react-embed"), { ssr: false })

const getRegistrationForm = (course: MappedCourse, capacityStatus: CheckCourseCapacity, lecturer: any, courseTechnology: CourseTechnology) => {
  if (course.courseVariant === CourseVariant.sampleCourse) {
    return <RegisterFormForSampleCourse course={course} lecturer={lecturer} capacityStatus={capacityStatus} courseTechnology={courseTechnology} />
  } else if (course.type === CourseType.preliminary) {
    return <RegisterPreliminaryForm />
  }

  return <RegistrationFormWithSteps course={course} lecturer={lecturer} capacityStatus={capacityStatus} courseTechnology={courseTechnology} />
}

const renderCourseRegistration = (
  course: MappedCourse,
  courseCapacityStatus: CheckCourseCapacity,
  technology: any,
  lecturer: any,
  courseTechnology: CourseTechnology
) => {
  if (course.isOpen && !courseCapacityStatus?.all.isFull) {
    return getRegistrationForm(course, courseCapacityStatus, lecturer, courseTechnology)
  }

  if (course.isBeforeRegistration) {
    return (
      <>
        <Typography.Title level={2}>Váš zájem nás zajímá</Typography.Title>
        <SubscribeForm
          tags={["Krouzky", "Připravujeme", technology.slug, course.name]}
          description="Kurz připravujeme. Vyplňte svůj email a my Vám dáme vědět, jakmile bude kurz připraven."
        />
      </>
    )
  }

  return null
}

const reactNodeIncludesText = (node: React.ReactNode, searchText: string): boolean => {
  if (typeof node === "string" || typeof node === "number") {
    return node.toString().includes(searchText)
  }

  if (Array.isArray(node)) {
    return node.some((child) => reactNodeIncludesText(child, searchText))
  }

  if (isValidElement(node)) {
    return reactNodeIncludesText(node.props.children, searchText)
  }

  return false
}

const RegistraceSlug = ({ slug, document, product, allContentfulProducts, ads }: Props) => {
  const course = useMemo(() => {
    if (document) {
      return getCourse(document?.course)
    }

    return getCourse(product)
  }, [document, product])
  const capacityData = useMemo(() => course && document && { capacity: course.capacity, courseSlug: course.slug }, [course, document])
  const courseCapacityStatus = useCourseCapacityStatus(course && document && capacityData)

  const courseUrl = `${webUrl}/registrace/${slug}`

  useEffect(() => {
    if (course && courseCapacityStatus && process.browser) {
      viewItemEvent({
        items: [
          {
            affiliation: "Website",
            currency: "CZK",
            item_brand: "VzdelaniBudoucnosti z.s.",
            item_category: course.courseVariant,
            item_id: course?.id ?? "N/A",
            item_name: course.name,
            item_variant: `${course.ageGroup}/${course.type}`,
            price: course.price,
            quantity: 1
          }
        ],
        currency: "CZK",
        value: course.price
      })
    }
  }, [course, courseCapacityStatus])

  const allAvailableContentfulProducts = getAvailableAndFilterOutProducts(course?.id, allContentfulProducts)

  if (!course) {
    return (
      <>
        <NextSeo
          title="Kurz nenalezen"
          description="Tento kurz jsme mohuýel nemohli najít."
          canonical={courseUrl}
          openGraph={{
            url: courseUrl,
            title: "Kurz nenalezen",
            description: "Tento kurz jsme mohuýel nemohli najít.",
            ...(course?.image?.url && {
              images: [
                {
                  url: course.image.url,
                  width: course.image.dimensions.width,
                  height: course.image.dimensions.height,
                  alt: course.image.alt ?? slug
                }
              ]
            })
          }}
        />

        <Layout>
          <SinglePage title="Kurz nenalezen" color={undefined} bgImage={undefined}>
            <Result
              status="404"
              title="Kurz nenalezen (404)"
              subTitle="Omlouváme se, ale detaily požadovaného kurzu se nám nepodařilo nalézt. Možná byl kurz odstraněn nebo je dočasně nedostupný. Zkuste to prosím později, nebo se podívejte na naše další kurzy. Děkujeme za pochopení."
              extra={
                <Link href="/">
                  <Button type="primary">Zpět na hlavní stránku</Button>
                </Link>
              }
            />

            <section>
              {allAvailableContentfulProducts?.length > 0 && (
                <CommonArticles>
                  <Typography.Title level={2}>Možná jste měli na mysli ...</Typography.Title>

                  <CoursesList
                    courses={allAvailableContentfulProducts}
                    productsColors={allContentfulProducts?.productsColors}
                    informationOnly={true}
                    css={css`
                      margin: 20px 0 0 0;
                    `}
                  />
                </CommonArticles>
              )}
            </section>
          </SinglePage>
        </Layout>
      </>
    )
  }

  const technology = getTechnology(document?.course ?? product)
  const technologySlugs = document?.course ? slugifyTechnology(document.course.data.technology) : { slugifyName: technology.slug }
  const feedbacks = document?.course ? getFeedBacks(document.authorWithFeedbacks?.feedbacks).slice(0, 2) : [] //TODO
  const lecturer = getLecturer(document?.authorWithFeedbacks ?? product)
  const pageTitle = course.name

  const seoTitle = adjustTitleLength(course)
  const seoDescription = generateSeoDescription(pageTitle, lecturer.name, slug, technology)

  const isCourseUnavailable: boolean = course.isAfterRegistration || courseCapacityStatus?.all.isFull

  const isCourseContentIncludesPriceAndTime: boolean =
    reactNodeIncludesText(course.content, "Délka") && reactNodeIncludesText(course.content, "Cena") && reactNodeIncludesText(course.content, "Kapacita")

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={courseUrl}
        openGraph={{
          url: courseUrl,
          title: seoTitle,
          description: seoDescription,
          ...(course?.image?.url && {
            images: [
              {
                url: course.image.url,
                width: course.image.dimensions.width,
                height: course.image.dimensions.height,
                alt: course.image.alt ?? slug
              }
            ]
          })
        }}
      />

      <Layout>
        <SinglePage title={pageTitle} color={technology.color} bgImage={course.image}>
          <SectionWithResetH2>
            <Row gutter={[40, 0]}>
              <Col
                md={16}
                css={css`
                  background-color: #fff;
                  padding: 20px 0 20px 0;
                `}>
                <Breadcrumb
                  css={css`
                    margin-bottom: 20px;
                  `}>
                  <Breadcrumb.Item>
                    <Link href="/">Hlavní strana</Link>
                  </Breadcrumb.Item>

                  {course.typeUrl ? (
                    <Breadcrumb.Item>
                      <Link href={course.typeUrl}>{getCourseAgeGroupLabel(course.ageGroup)}</Link>
                    </Breadcrumb.Item>
                  ) : null}

                  {technology?.name ? (
                    <Breadcrumb.Item>
                      <Link href={course.technologyUrl}>{technology.name}</Link>
                    </Breadcrumb.Item>
                  ) : null}

                  <Breadcrumb.Item>
                    <Link href={course.registerUrl}>{course.name}</Link>
                  </Breadcrumb.Item>
                </Breadcrumb>

                {isCourseUnavailable ? <CourseCloseAlert style={{ marginBottom: 20 }} course={course} /> : null}

                <StyledParagraph>{replaceUrlsWithLinks(course.additionalInformation)}</StyledParagraph>

                {replaceUrlsWithLinks(course.content)}

                {!isCourseContentIncludesPriceAndTime ? (
                  <>
                    <Typography.Title level={2}>Kdy a jak dlouho?</Typography.Title>
                    <StyledParagraph>
                      {course.when}{" "}
                      {course.lessonCount === 1
                        ? "1 lekce trvající 60 minut."
                        : `Kurz zahrnuje ${course.lessonCount} lekcí po 60 minutách, tedy celkem ${
                            course.lessonCount * 60
                          } minut objevování, tvoření a zábavy.`}
                    </StyledParagraph>

                    <CoursePrice course={course} />

                    {course.courseVariant !== CourseVariant.sampleCourse ? (
                      <>
                        <Typography.Title level={2}>Počet účastníků</Typography.Title>
                        <StyledParagraph>
                          Abychom zajistili maximální pozornost každému dítěti, je kapacita kroužku omezena na {course.capacity} účastníků.
                        </StyledParagraph>
                      </>
                    ) : null}
                  </>
                ) : null}

                <Typography.Title level={2}>Kde kroužek probíhá?</Typography.Title>
                <StyledParagraph>{replaceUrlsWithLinks(course.where)}</StyledParagraph>

                <SmallScreenOnly>
                  {course.when ? (
                    <>
                      <Typography.Title level={2}>Kdy?</Typography.Title>
                      <StyledParagraph>{replaceUrlsWithLinks(course.when)}</StyledParagraph>
                    </>
                  ) : null}

                  {course.where ? (
                    <>
                      <Typography.Title level={2}>Kde kroužek probíhá?</Typography.Title>
                      <StyledParagraph>{replaceUrlsWithLinks(course.where)}</StyledParagraph>
                    </>
                  ) : null}

                  {course.additionalInformation && course.additionalInformation !== "" ? (
                    <>
                      <Typography.Title level={2}>Další údaje</Typography.Title>
                      <StyledParagraph>{replaceUrlsWithLinks(course.additionalInformation)}</StyledParagraph>
                    </>
                  ) : null}
                </SmallScreenOnly>

                <SimilarOrRelatedProducts allCourses={allContentfulProducts} currentCourse={course} currentTechnology={technology} />

                {course.type === CourseType.online && (
                  <section>
                    <Typography.Title level={2}>Co je potřeba k účasti?</Typography.Title>
                    <StyledParagraph>
                      Pro účast v kurzu stačí základní dovednosti s počítačem, mikrofon a stabilní internet. Výuka probíhá online přes Google Meet. Podrobnosti
                      najdete v článku:{" "}
                      <Link href="/blog/krouzky-pro-deti/podminky-pro-ucastniky-d773941-aBXqiRMAACwA2kLS" target="_blank">
                        Podmínky pro účastníky
                      </Link>
                    </StyledParagraph>
                  </section>
                )}

                {course.type !== CourseType.online && (
                  <>
                    <Typography.Title level={2}>Co je potřeba k účasti?</Typography.Title>
                    <StyledParagraph>
                      Pro účast v kurzu je potřeba pouze základní dovednosti v ovládání počítače. Nevyžaduje se žádná speciální licence k softwaru.
                    </StyledParagraph>
                  </>
                )}

                <PhotoGallery photoGalleryImages={course.photoGallery}>
                  <Typography.Title
                    level={2}
                    css={css`
                      margin-top: 0.6em;
                    `}>
                    Ukázky z výuky
                  </Typography.Title>
                </PhotoGallery>

                {course.sampleCourseVideoUrl && (
                  <SampleCourseVideo
                    courseName={course.name}
                    courseSlug={course.slug}
                    courseRegistrationUrl={`${getWebSiteUrlFromWindow() || "https://vzdelanibudoucnosti.cz"}${
                      course.registerUrl
                    }?utm_source=link&utm_medium=email&utm_campaign=sample-course-email`}
                    sampleCourseVideoUrl={course.sampleCourseVideoUrl}
                  />
                )}

                {courseCapacityStatus?.all.isAlmostFull && course.isOpen && (
                  <Alert
                    css={css`
                      margin-top: 40px;
                      border-radius: 20px;
                    `}
                    message=""
                    description="Kurz bude brzy naplněn. Zaregistrujte se nyní!"
                    type="warning"
                    showIcon
                  />
                )}

                {renderCourseRegistration(course, courseCapacityStatus, technology, lecturer, technology)}

                <Typography.Title level={2}>Co následuje po kurzu?</Typography.Title>
                <StyledParagraph>
                  Každý účastník získá certifikát se svým jménem a podpisem lektora. Tento certifikát může sloužit jako potvrzení o absolvování, příloha k
                  přihlášce na školu nebo jako motivace k dalšímu vzdělávání.
                </StyledParagraph>
                <StyledParagraph>
                  Absolvováním kurzu to ale nekončí – děti mohou pokračovat v navazujících kurzech, nebo využít získané dovednosti ve vlastních projektech. Naši
                  lektoři rádi poradí, jaké kurzy nebo výzvy jsou vhodné jako další krok.
                </StyledParagraph>

                {course.similarOrRelatedProducts.length > 0 && course.courseVariant === CourseVariant.sampleCourse ? (
                  <>
                    <Typography.Title level={2}>Zajistěte si místo v našich kurzech již dnes</Typography.Title>
                    <Alert
                      css={css`
                        border-radius: 20px;
                      `}
                      message=""
                      description={
                        <>
                          <StyledParagraph>
                            Pokud jste rozhodnuti, můžete se již nyní přihlásit jak na ukázkovou hodinu, tak na velký placený kurz. Není nutné platit velký
                            placený kurz ihned. Nezapomeňte si zajistit místo zde:
                          </StyledParagraph>

                          {course.similarOrRelatedProducts.map((d) => (
                            <div key={d.id}>
                              <Link href={d.registerUrl}>{d.name}</Link>
                            </div>
                          ))}
                        </>
                      }
                      type="warning"
                      showIcon
                    />
                  </>
                ) : null}
              </Col>

              <Col
                md={8}
                css={css`
                  padding: 20px 0 20px 0;
                `}>
                <Space direction="vertical" style={{ display: "initial" }}>
                  <div
                    css={css`
                      margin-bottom: 20px;

                      .ant-list-sm .ant-list-item {
                        padding: 8px 0;
                      }
                    `}>
                    <Typography.Title level={4}>Základní informace</Typography.Title>
                    <List
                      size="small"
                      css={css`
                        padding: 14px;
                        background-color: #fff;
                        border-radius: 20px;

                        .ant-list-item {
                          &:first-of-type {
                            padding-top: 0;
                          }

                          &:last-of-type {
                            padding-bottom: 0;

                            .ant-typography {
                              &:last-of-type {
                                margin-bottom: 0;
                              }
                            }
                          }
                        }
                      `}>
                      {course.when ? (
                        <ListItem>
                          <Typography.Title level={5}>Kdy?</Typography.Title>
                          <Typography.Text style={{ fontSize: 12, marginBottom: 16 }}>{course.when}</Typography.Text>

                          <MiniScheduleCalendar courseDates={course.courseLessons} firstCourseDay={course.firstCourseDay} />
                        </ListItem>
                      ) : null}

                      {course.where ? (
                        <ListItem>
                          <Typography.Title level={5}>Kde?</Typography.Title>
                          <Typography.Text style={{ fontSize: 12 }}>{replaceUrlsWithLinks(course.where)}</Typography.Text>
                        </ListItem>
                      ) : null}

                      {course.price ? (
                        <ListItem>
                          <Typography.Title level={5}>Cena</Typography.Title>
                          <Typography.Text style={{ fontSize: 12 }}>{`${course.price ?? 0} Kč`}</Typography.Text>
                        </ListItem>
                      ) : null}

                      {course.additionalInformation && course.additionalInformation !== "" && (
                        <ListItem
                          style={{ fontSize: 12 }}
                          css={css`
                            *,
                            strong {
                              font-weight: normal;
                            }
                          `}>
                          {replaceUrlsWithLinks(course.additionalInformation)}
                        </ListItem>
                      )}
                    </List>
                  </div>

                  {courseCapacityStatus?.all.isAlmostFull && !courseCapacityStatus?.all.isFull && (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <Alert
                        style={{ fontSize: 12 }}
                        css={css`
                          border-radius: 20px;
                        `}
                        type="warning"
                        banner
                        message="Kurz bude brzy naplněn. Zaregistrujte se nyní!"
                      />
                    </div>
                  )}

                  {course.subCourse && (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <Typography.Title level={4}>Ukázková hodina</Typography.Title>

                      <div
                        css={css`
                          padding: 14px;
                          background-color: #fff;
                          border-radius: 20px;
                        `}>
                        <StyledParagraph style={{ fontSize: 12 }}>
                          Nevíte, zda je kurz vhodný a bude bavit? Přihlašte se na bezplatnou ukázkovou hodinu.
                        </StyledParagraph>

                        <Link href={course.subCourse.registerUrl}>Přejít na ukázkovou hodinu</Link>
                      </div>
                    </div>
                  )}

                  {course?.embeddedContentVideoUrls?.length > 0 &&
                    course.embeddedContentVideoUrls.map((u) => (
                      <NoSSREmbed
                        key={u}
                        url={u}
                        renderWrap={(children) => (
                          <div
                            css={css`
                              border-radius: 20px;
                              overflow: hidden;
                              margin-bottom: 20px;

                              > div {
                                position: relative;
                                padding-bottom: 56.25%; /* 16:9 */
                                height: 0;

                                iframe {
                                  position: absolute;
                                  top: 0;
                                  left: 0;
                                  width: 100%;
                                  height: 100%;
                                }
                              }
                            `}>
                            {children}
                          </div>
                        )}
                      />
                    ))}

                  {technology.description && technology.name ? (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <Typography.Title level={4}>Co je to {technology.name}?</Typography.Title>

                      <div
                        css={css`
                          padding: 14px;
                          background-color: #fff;
                          border-radius: 20px;
                        `}>
                        <StyledParagraph style={{ fontSize: 12 }}>{technology.description}</StyledParagraph>

                        <Link href={`/pocitacove-krouzky-pro-deti/${technologySlugs.slugifyName}`}>Více informací</Link>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}

                  {lecturer && lecturer.name !== "" && (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <Typography.Title level={4}>Lektor</Typography.Title>
                      <Row
                        align="middle"
                        gutter={[0, 8]}
                        css={css`
                          padding: 14px;
                          background-color: #fff;
                          border-radius: 20px;
                          flex-flow: row;
                        `}>
                        {lecturer.pictureUrl && (
                          <Col
                            css={css`
                              width: 90px;
                              height: 90px;
                              margin-right: 10px;
                            `}>
                            <Link href={lecturer.more} target={`_blank`}>
                              <ImageRounded
                                src={lecturer.pictureUrl}
                                alt={`lektor-${lecturer.name}`}
                                width={90}
                                height={90}
                                style={{ objectFit: "cover" }}
                                sizes="100vw"
                                css={css`
                                  transition: all 0.2s ease-in-out;
                                  filter: grayscale(100%);

                                  &:hover {
                                    transform: scale(1.4);
                                  }
                                `}
                              />
                            </Link>
                          </Col>
                        )}
                        <Col>
                          <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            <Link href={lecturer.more} target="_blank">
                              {lecturer.name}
                            </Link>
                          </Typography.Title>
                          <StyledParagraph style={{ fontSize: 12 }}>
                            {lecturer.position}
                            <br />
                            <Link href={`mailto:${lecturer.email}`} title="email">
                              <EmailWithBreak email={lecturer.email} />
                            </Link>
                          </StyledParagraph>

                          <Link href={lecturer.more} target="_blank">
                            Více o lektorovi
                          </Link>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {lecturer && lecturer.name != "" && (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <Typography.Title level={4}>Kontaktní formulář</Typography.Title>
                      <Row
                        align="middle"
                        gutter={[0, 8]}
                        css={css`
                          padding: 14px;
                          background-color: #fff;
                          border-radius: 20px;
                          flex-flow: row;
                        `}>
                        <Col>
                          <ContactForm
                            sendTo={lecturer.email}
                            contactFormName={course.name}
                            contactFormUrl={courseUrl}
                            externalCss={css`
                              padding: 0;
                              margin-top: 0;
                              border-radius: 0;

                              ${md} {
                                padding: 0;
                              }

                              .ant-row {
                                flex-direction: column;
                              }

                              .ant-form-item-label {
                                flex: auto;
                                max-width: max-content;
                              }

                              .ant-form-item-control {
                                max-width: none;
                              }

                              .ant-input {
                                font-size: 12px;
                              }

                              textarea.ant-input {
                                padding: 6px 6px;
                              }
                            `}>
                            <StyledParagraph style={{ fontSize: 12 }}>
                              V případě jakýchkoli otázek se na nás neváhejte obrátit. Váš dotaz pošleme také lektorovi.
                            </StyledParagraph>
                          </ContactForm>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {feedbacks?.length > 0 && (
                    <div
                      css={css`
                        margin-bottom: 20px;
                      `}>
                      <div>
                        <Typography.Title level={4}>Hodnocení</Typography.Title>

                        <div
                          css={css`
                            margin-bottom: 20px;
                          `}>
                          <Comments
                            commentStyle={{ fontSize: 12, lineHeight: 1.2 }}
                            css={css`
                              padding: 0 15px 0 15px;
                              line-height: 1.3;
                            `}
                            comments={feedbacks.map((f, i) => ({
                              dateTime: f.dateTime,
                              avatarImagePath: f.imageUrl || getFeedbackAvatarUrl(f.sex, i),
                              content: f.description,
                              author: f.name
                            }))}
                          />
                          <div
                            css={css`
                              padding-top: 10px;
                            `}>
                            <Link href="/recenze">Další recenze</Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Space>
              </Col>
            </Row>
          </SectionWithResetH2>

          <section
            css={css`
              margin-top: 20px;
            `}>
            {allAvailableContentfulProducts.length > 0 && (
              <div>
                <CommonArticles>
                  <Typography.Title level={2}>Nabídka dalších kroužků</Typography.Title>

                  <CoursesList
                    courses={allAvailableContentfulProducts}
                    productsColors={allContentfulProducts?.productsColors}
                    informationOnly={true}
                    css={css`
                      margin: 20px 0 0 0;
                    `}
                  />
                </CommonArticles>
              </div>
            )}
          </section>

          <section>
            <Ads ads={ads} />
          </section>

          {!course.isBeforeRegistration && (
            <Section>
              <Typography.Title level={2}>Odběr novinek</Typography.Title>
              <SubscribeForm tags={["krouzky", "registrace", technology.name, course.slug, course.ageGroup, course.type]} />
            </Section>
          )}
        </SinglePage>
      </Layout>
    </>
  )
}

const ListItem = styled(List.Item)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > h5 {
    margin-bottom: 0.25em;
  }
`

const SectionWithResetH2 = styled.section`
  .ant-typography + h1.ant-typography,
  .ant-typography + h2.ant-typography,
  .ant-typography + h3.ant-typography,
  .ant-typography + h4.ant-typography,
  .ant-typography + h5.ant-typography {
    margin-top: 0.6em;
  }
`

interface Props {
  slug: string
  document: Course | null
  product: Entry<TypeProductEntitySkeleton, undefined, string> | null
  ads: AdsWithColors
  allContentfulProducts: AllProduct | null
}

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const prismicCourses = await getCourseList()
  const contentfulCourses = await getAllProducts({ "fields.subType[in]": ["course", "sampleCourse"] })

  const pathsWithId = (prismicCourses?.results || []).map((d: PrismicDocument<any>) => {
    const { slug } = slugifyCourse(d)
    return { params: { slug } }
  })

  const contentfulPaths = (contentfulCourses?.products?.items || []).map((p) => {
    return { params: { slug: getProductSlug(p) } }
  })

  return {
    paths: [...pathsWithId, ...contentfulPaths],
    fallback: true
  }
}

export const getStaticProps: GetStaticProps = async ({ params }): Promise<GetStaticPropsResult<Props>> => {
  const slug = isArray(params.slug) ? params.slug[0] : params.slug

  const ads = await getAllVzAds()
  const allContentfulProducts = await getAllProducts()

  const emptyProps: Props = {
    ads,
    document: null,
    product: null,
    slug,
    allContentfulProducts
  }

  const document = await getCourseBySlugLegacy(slug)
  if (!!document?.course?.id) {
    return {
      props: {
        slug,
        document,
        product: null,
        ads,
        allContentfulProducts
      },
      revalidate: dev ? 1 : 120
    }
  }

  const contentfulSlugAndId = extractSlugAndId(slug)
  const productResult = await getProductByEntryId(contentfulSlugAndId?.id)
  const product = productResult?.entry
  if (!!product?.sys?.id) {
    return {
      props: {
        slug,
        document: null,
        product,
        ads,
        allContentfulProducts
      },
      revalidate: dev ? 1 : 120
    }
  }

  return {
    props: {
      ...emptyProps,
      allContentfulProducts: {
        ...allContentfulProducts,
        products: {
          ...allContentfulProducts.products,
          items: sortSimilarProducts(allContentfulProducts.products.items, slug)
        }
      }
    },
    revalidate: dev ? 1 : 120
  }
}

export default RegistraceSlug
